import { analyticsService } from '@meiuca/analytics';
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SignUpFlow } from '../components/SignUpFlow';
import { useAuth } from '../contexts/AuthContext';
import { useAuthFlow } from '../hooks/useAuthFlow';
import { ArrowLeft } from 'lucide-react';

export default function SignUpPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();
  const { navigateToCorrectPage, callbackUrl } = useAuthFlow();

  useEffect(() => {
    analyticsService.trackScreenView('signup');
  }, []);

  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      navigateToCorrectPage();
    }
  }, [isAuthenticated, isLoading, navigateToCorrectPage]);

  const handleSignUpSuccess = () => {
    console.log('Signup success called!');
    // Use o hook useAuthFlow para determinar onde navegar
    if (callbackUrl) {
      navigate(decodeURIComponent(callbackUrl));
    } else {
      navigate('/');
    }
  };

  const handleLoginClick = () => {
    // Preserva o callback URL quando navega para login
    const loginUrl = callbackUrl ? `/login?callback=${encodeURIComponent(callbackUrl)}` : '/login';
    navigate(loginUrl);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-primary/5 via-background-primary to-brand-secondary/5 flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="w-8 h-8 border-2 border-brand-primary border-t-transparent rounded-full"
        />
      </div>
    );
  }

  if (isAuthenticated) {
    return null; // Will redirect to appropriate page
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-primary/5 via-background-primary to-brand-secondary/5">
      {/* Background Animation */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute -top-40 -right-40 w-80 h-80 bg-brand-primary/10 rounded-full"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        
        <motion.div
          className="absolute -bottom-40 -left-40 w-96 h-96 bg-brand-secondary/10 rounded-full"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 2,
          }}
        />

        {/* Floating particles - Use static positions to avoid hydration issues */}
        {Array.from({ length: 8 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-brand-secondary/20 rounded-full"
            style={{
              left: `${(i * 12 + 8)}%`,
              top: `${(i * 8 + 15)}%`,
            }}
            animate={{
              y: [0, -30, 0],
              opacity: [0.2, 0.8, 0.2],
            }}
            transition={{
              duration: i * 0.5 + 3,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: i * 0.25,
            }}
          />
        ))}
      </div>

      {/* Back Link */}
      <motion.button
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        onClick={() => navigate('/')}
        className="absolute top-4 left-4 z-20 flex items-center space-x-2 text-text-secondary hover:text-text-primary transition-colors"
      >
        <ArrowLeft className="w-5 h-5" />
        <span>Voltar</span>
      </motion.button>

      {/* Main Content */}
      <div className="relative z-10 flex items-center justify-center min-h-screen p-4">
        <div className="w-full max-w-md">
          {/* Background Card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="bg-background-white rounded-2xl shadow-2xl p-8 backdrop-blur-sm border border-white/20"
          >
            <SignUpFlow
              onSuccess={handleSignUpSuccess}
              onLoginClick={handleLoginClick}
            />
          </motion.div>

          {/* Footer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-center mt-8"
          >
            <p className="text-sm text-text-tertiary">
              © 2025 Meiuca App. Todos os direitos reservados.
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
